import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@heidi/design-system/page-shell';
import { useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { RecentChatRow } from '../recent-chats/recent-chat-row';
import {
  InertRecentVisitHoverCardHost,
  RecentVisitHoverCardHost,
} from '../recent-visits/recent-visit-hover-card-host';
import { RecentVisitRow } from '../recent-visits/recent-visit-row';
import type { RecentItem } from './merge-recent-items';
import { getRecentsDateGroupLabel, groupRecentItemsByDate } from './recents-date-group';
import { RecentsDateGroupHeader } from './recents-date-group-header';
import { RecentsSearchFeedback } from './recents-search-feedback';
import { useRecentsDateGroupCollapse } from './use-recents-date-group-collapse';

interface RecentsSectionBodyProps {
  /** The Home hover preview's reuse of this list (`RecentsSection`'s
   *  `isCompact`). Turns the nested encounter hover card OFF — see
   *  `InertRecentVisitHoverCardHost` — while leaving every other row
   *  behavior (grouping, collapse, rendering) unchanged. */
  isCompact?: boolean;
  isSearching: boolean;
  isSearchEmpty: boolean;
  hasSearchError: boolean;
  showPartialSearchError: boolean;
  isBrowseFilterEmpty: boolean;
  isBrowseLoading: boolean;
  isSearchLoading: boolean;
  typeFilteredItems: RecentItem[];
  attentionProjectIds: Set<string>;
  includesVisits: boolean;
  includesChats: boolean;
  isErrorVisits: boolean;
  isErrorChats: boolean;
  refetchVisits: () => unknown;
  refetchChats: () => unknown;
  skeletonRows: number;
  currentChatId: string | null;
  /** The pagination control, or nothing when there is no next page. While
   *  browsing by date it lives inside the last group's collapsible content —
   *  the rows it extends — so collapsing that group hides it too. */
  loadMore?: ReactNode;
}

const renderRecentItem = (item: RecentItem, attentionProjectIds: Set<string>) =>
  item.kind === 'visit' ? (
    <RecentVisitRow key={item.key} session={item.visit} />
  ) : (
    <RecentChatRow
      key={item.key}
      chat={item.chat}
      needsAttention={attentionProjectIds.has(item.chat.id)}
    />
  );

export const RecentsSectionBody = ({
  isCompact = false,
  isSearching,
  isSearchEmpty,
  hasSearchError,
  showPartialSearchError,
  isBrowseFilterEmpty,
  isBrowseLoading,
  isSearchLoading,
  typeFilteredItems,
  attentionProjectIds,
  includesVisits,
  includesChats,
  isErrorVisits,
  isErrorChats,
  refetchVisits,
  refetchChats,
  skeletonRows,
  currentChatId,
  loadMore = null,
}: RecentsSectionBodyProps) => {
  const intl = useIntl();
  const groupedItems = useMemo(
    () => (isSearching ? [] : groupRecentItemsByDate(typeFilteredItems, new Date())),
    [isSearching, typeFilteredItems]
  );
  const { isGroupCollapsed, setGroupOpen } = useRecentsDateGroupCollapse({
    currentChatId,
    typeFilteredItems,
    isSearching,
  });

  if (isSearchEmpty) {
    return (
      <RecentsSearchFeedback
        isError={hasSearchError}
        retryVisits={includesVisits && isErrorVisits ? refetchVisits : undefined}
        retryChats={includesChats && isErrorChats ? refetchChats : undefined}
      />
    );
  }

  // Compact (the Home hover preview) turns the nested card off entirely — an
  // inert provider, not a skipped one, since `RecentVisitRow` throws without
  // *a* controller in context. The sidebar variant keeps the real, shared
  // card: one instance for every visit row below, across all date groups, so
  // moving the pointer down the list swaps its content instead of closing
  // and reopening at each step. Neither renders any DOM of its own.
  const HoverCardHost = isCompact
    ? InertRecentVisitHoverCardHost
    : RecentVisitHoverCardHost;

  return (
    <HoverCardHost>
      {showPartialSearchError && (
        <RecentsSearchFeedback
          isError
          retryVisits={includesVisits && isErrorVisits ? refetchVisits : undefined}
          retryChats={includesChats && isErrorChats ? refetchChats : undefined}
        />
      )}
      {isBrowseFilterEmpty ? (
        <>
          <RecentsSearchFeedback isError={false} />
          {loadMore}
        </>
      ) : isBrowseLoading || isSearchLoading ? (
        <SidebarMenu>
          {Array.from({ length: skeletonRows }, (_, index) => (
            <SidebarMenuItem key={index}>
              <SidebarMenuSkeleton showIcon />
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      ) : isSearching ? (
        <>
          <SidebarMenu>
            {typeFilteredItems.map((item) => renderRecentItem(item, attentionProjectIds))}
          </SidebarMenu>
          {loadMore}
        </>
      ) : groupedItems.length === 0 ? (
        loadMore
      ) : (
        // gap-2 between date groups sits between SidebarMenu's gap-1 item
        // rhythm and the rail's section spacing, so groups read as distinct.
        <div className="flex flex-col gap-2">
          {groupedItems.map(({ group, items }, index) => (
            <RecentsDateGroupHeader
              key={group}
              group={group}
              label={getRecentsDateGroupLabel(group, intl)}
              isOpen={!isGroupCollapsed(group)}
              onOpenChange={(open) => setGroupOpen(group, open)}
              isCompact={isCompact}
              footer={index === groupedItems.length - 1 ? loadMore : undefined}
            >
              {items.map((item) => renderRecentItem(item, attentionProjectIds))}
            </RecentsDateGroupHeader>
          ))}
        </div>
      )}
    </HoverCardHost>
  );
};
